"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Building2, HardHat, MapPin, Ruler, ShieldCheck, Users } from "lucide-react";
const stats = [{
  value: "98%",
  label: "Clinician Retention"
}, {
  value: "15k+",
  label: "Annual Procedures"
}, {
  value: "100%",
  label: "Board Certified"
}, {
  value: "50+",
  label: "Partner Facilities"
}];
const clients = [{
  name: "Amazon",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/logo_amazon_client.png"
}, {
  name: "Imperial Logistics",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/logo_imperial_client.png"
}, {
  name: "CEVA",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/logo_ceva_client.png"
}, {
  name: "Takealot",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1613743/img/logo_takealot_client.png"
}];
const services = [{
  title: "Surgical Assistants (CSA)",
  icon: Building2,
  desc: "Elite clinical support for complex operative procedures and surgical workflows."
}, {
  title: "Certified First Surgical Assistants (CSFA)",
  icon: HardHat,
  desc: "Highly trained assistants focused on surgical precision and operating room efficiency."
}, {
  title: "First Assist Coverage",
  icon: ShieldCheck,
  desc: "Reliable first assist support for hospitals and ambulatory surgery centers."
}, {
  title: "RNFA Support",
  icon: Users,
  desc: "Expert Registered Nurse First Assistants integrated into your perioperative team."
}, {
  title: "Perioperative Staffing",
  icon: MapPin,
  desc: "Comprehensive staffing solutions for the entire perioperative continuum."
}, {
  title: "Procedure-Based Coverage",
  icon: Ruler,
  desc: "Flexible staffing models tailored to specific surgical case volumes and types."
}, {
  title: "Recurring Coverage",
  icon: ArrowRight,
  desc: "Consistent, long-term staffing partnerships to ensure clinical continuity."
}];
export default function About() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: isStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="148842" id="flexiblestaffingsolutions" className="bg-background text-foreground py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="lg:col-span-12 text-center flex flex-col items-center">
            <h2 className="font-semibold tracking-tight text-3xl md:text-5xl text-primary mb-8">
              Flexible Staffing Solutions
            </h2>
            <Separator className="mb-8 bg-border/20 h-[2px] max-w-2xl" />
            <div className="text-lg md:text-xl text-muted-foreground font-normal leading-relaxed max-w-3xl mx-auto">
              <p className="break-inside-avoid">
                Atlas Surgical Partners provides staffing support designed specifically for surgical and perioperative environments.
              </p>
            </div>
          </div>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24 transition-all duration-1000 delay-200 ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="lg:col-span-12">
            <Tabs defaultValue="services" className="w-full">
              <TabsList className="flex w-fit mx-auto rounded-none bg-muted/30 p-1 mb-8">
                <TabsTrigger value="services" className="rounded-none uppercase tracking-widest font-bold text-xs md:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Clinical Specialties
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="services" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service, i) => <Card key={i} data-index={i} className="text-card-foreground flex flex-col gap-6 py-6 rounded-xl border-border/10 bg-card transition-all duration-300 !shadow-[0px_4px_8px_rgba(20,21,31,0.18),_0px_2px_4px_rgba(20,21,31,0.13)] hover:shadow-md">
                      <CardHeader className="pb-2">
                        <service.icon className="size-8 text-secondary mb-2" strokeWidth={1.5} />
                        <CardTitle className="text-lg uppercase tracking-tight text-primary">{service.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{service.desc}</p>
                      </CardContent>
                    </Card>)}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        
      </div>
    </section>;
}